import { Routes } from '@angular/router';
import { environment } from '@environments/environment';
import { OktaAuthGuard, OktaCallbackComponent } from '@okta/okta-angular';
import { genesysCloudOnlyGuard, searchEngineGuard, searchRecommendationsEngineGuard } from '@shared/guards';
import { digitalAssistantGuard } from './@shared/guards/digital-assistant.guard';
import { renewalFlowGuard } from './@shared/guards/renewal-flow.guard';
import { ChildPageRoute, GknRoutePath } from './app-routing.enum';
import { gcTokenGuard } from './billing/shared/service/gc-token.guard';
import { HomeComponent } from './home/home.component';
import { SsoCallbackComponent } from './sso-callback/sso-callback.component';

export const gknRouteConfig: Routes = [
    {
        // Warning: This "HOME" path maps to a field in Okta's Admin dashboard.
        // Do not change it without updating the Okta app (done through IT).
        path: GknRoutePath.HOME,
        children: [
            {
                path: ChildPageRoute.ROADMAP_ARCHIVES,
                data: { breadcrumb: 'Genesys Cloud Roadmap Archives' },
                canActivate: [OktaAuthGuard, genesysCloudOnlyGuard],
                loadComponent: () => import('./archives/archives.component').then((c) => c.ArchivesComponent),
            },
            {
                path: '',
                component: HomeComponent,
            },
        ],
    },
    {
        // Warning: This path maps to a field in Okta's admin dashboard.
        // Do not change it without updating the Okta app (done through IT).
        path: GknRoutePath.LOGIN_CALLBACK,
        component: OktaCallbackComponent,
    },
    {
        path: GknRoutePath.SSO_CALLBACK,
        component: SsoCallbackComponent,
    },
    {
        path: GknRoutePath.PROFILE,
        data: { breadcrumb: 'Profile' },
        children: [
            {
                path: ChildPageRoute.ACHIEVERS_LOUNGE,
                data: { breadcrumb: "Orchestrators Achiever's Lounge" },
                loadComponent: () =>
                    import('./profile/achv-lounge/achv-lounge.component').then((c) => c.AchieversLoungeComponent),
            },
            {
                path: '',
                data: { breadcrumb: 'Profile' },
                loadComponent: () => import('./profile/profile.component').then((c) => c.ProfileComponent),
            },
        ],
        canActivate: [OktaAuthGuard],
    },
    {
        path: GknRoutePath.ADVOCACY,
        loadComponent: () => import('./advocacy/advocacy.component').then((m) => m.AdvocacyComponent),
    },
    {
        path: GknRoutePath.COMMUNITY,
        loadComponent: () => import('./community/community.component').then((c) => c.CommunityComponent),
    },
    {
        path: GknRoutePath.EDUCATION,
        loadComponent: () => import('./education/education.component').then((c) => c.EducationComponent),
    },
    {
        path: GknRoutePath.RESOURCES,
        data: { breadcrumb: 'Resources' },
        children: [
            {
                path: ChildPageRoute.ONBOARDING,
                data: { breadcrumb: 'Onboarding' },
                canActivate: [genesysCloudOnlyGuard],
                children: [
                    {
                        path: `${ChildPageRoute.STARTER_KITS}/:id`,
                        data: { breadcrumb: 'Starter Kit' },
                        loadComponent: () =>
                            import('./resources/onboarding/starter-kit/starter-kit.component').then(
                                (c) => c.StarterKitComponent,
                            ),
                    },
                    {
                        path: `${ChildPageRoute.BOOST_YOUR_KNOWLEDGE_WEBINARS_LIBRARY}`,
                        data: { breadcrumb: 'Onboarding Webinars Library' },
                        loadComponent: () =>
                            import('./resources/onboarding/onboarding-webinars-library/onboarding-webinars-library.component').then(
                                (c) => c.OnboardingWebinarsLibraryComponent,
                            ),
                    },
                    {
                        path: '',
                        loadComponent: () =>
                            import('./resources/onboarding/onboarding.component').then((c) => c.OnboardingComponent),
                    },
                ],
            },
            {
                data: { breadcrumb: 'Capability Accelerator' },
                path: ChildPageRoute.CAPABILITY_ACCELERATOR,
                canActivate: [genesysCloudOnlyGuard],
                children: [
                    {
                        path: ':id',
                        loadComponent: () =>
                            import('./resources/onboarding/starter-kit/starter-kit.component').then(
                                (c) => c.StarterKitComponent,
                            ),
                    },
                    {
                        path: '',
                        loadComponent: () =>
                            import('./resources/capability-accelerator/capability-accelerator.component').then(
                                (c) => c.CapabilityAcceleratorComponent,
                            ),
                    },
                ],
            },
            // {
            //     path: ChildPageRoute.CX_EVOLUTION,
            //     data: { breadcrumb: 'CX Evolution' },
            //     loadComponent: () =>
            //         import('./resources/cx-evolution/cx-evolution.component').then((c) => c.CxEvolutionComponent),
            // },
            {
                path: ChildPageRoute.PRODUCT_KNOWLEDGE,
                data: { breadcrumb: 'Product Knowledge' },
                loadComponent: () =>
                    import('./resources/product-knowledge/product-knowledge.component').then(
                        (c) => c.ProductKnowledgeComponent,
                    ),
            },
            {
                path: ChildPageRoute.GENESYS_NEWS_AND_EVENTS,
                data: { breadcrumb: 'Genesys news and events' },
                loadComponent: () =>
                    import('./resources/genesys-news-events/genesys-news-events.component').then(
                        (c) => c.GenesysNewsEventsComponent,
                    ),
            },
            {
                path: ChildPageRoute.APPFOUNDRY,
                data: { breadcrumb: 'AppFoundry' },
                canActivate: [genesysCloudOnlyGuard],
                loadComponent: () =>
                    import('./resources/appfoundry/appfoundry.component').then((c) => c.AppFoundryComponent),
            },
            {
                path: '',
                redirectTo: 'product-knowledge',
                pathMatch: 'prefix',
            },
        ],
    },
    {
        path: GknRoutePath.SUPPORT,
        data: { breadcrumb: 'Support' },
        children: [
            {
                path: ChildPageRoute.CASE_MANAGEMENT,
                data: { breadcrumb: 'Case management' },
                canActivate: [OktaAuthGuard],
                loadComponent: () =>
                    import('./support/case-management/case-management.component').then(
                        (c) => c.CaseManagementComponent,
                    ),
            },
            {
                path: ChildPageRoute.PRODUCT_SUPPORT_RESOURCES,
                data: { breadcrumb: 'Product Support resources' },
                loadComponent: () =>
                    import('./support/product-support-resources/product-support-resources.component').then(
                        (c) => c.ProductSupportResourcesComponent,
                    ),
            },
            {
                path: ChildPageRoute.PRODUCT_ANNOUNCEMENTS,
                data: { breadcrumb: 'Product announcements' },
                loadComponent: () =>
                    import('./support/product-announcements-page/product-announcements-page.component').then(
                        (c) => c.ProductAnnouncementsPageComponent,
                    ),
            },
            {
                path: '',
                redirectTo: ChildPageRoute.PRODUCT_SUPPORT_RESOURCES,
                pathMatch: 'full',
            },
        ],
    },
    {
        path: GknRoutePath.SEARCH,
        canActivate: [searchEngineGuard, searchRecommendationsEngineGuard],
        loadComponent: () =>
            import('./search-results/headless-search/headless-search.component').then((c) => c.HeadlessSearchComponent),
    },
    {
        path: GknRoutePath.SIGNUP,
        data: { breadcrumb: 'Genesys Account Registration' },
        loadComponent: () => import('./sign-up/sign-up.component').then((c) => c.SignupComponent),
    },
    // Core Team only routes
    {
        path: GknRoutePath.USER_MANAGEMENT,
        loadComponent: () =>
            import('./user-management/user-management.component').then((m) => m.UserManagementComponent),
        canActivate: [OktaAuthGuard],
    },
    {
        path: GknRoutePath.BILLING,
        canActivate: [OktaAuthGuard],
        children: [
            {
                path: ChildPageRoute.BILLING_OAUTH_CALLBACK,
                loadComponent: () =>
                    import('./billing/components/billing-oauth-callback/billing-oauth-callback.component').then(
                        (c) => c.BillingOAuthCallbackComponent,
                    ),
            },
            {
                path: ChildPageRoute.BILLING_ACCESS_DENIED,
                loadComponent: () =>
                    import('./billing/components/billing-access-denied/billing-access-denied.component').then(
                        (c) => c.BillingAccessDeniedComponent,
                    ),
            },
            {
                path: '',
                canActivate: [gcTokenGuard],
                loadComponent: () => import('./billing/billing.component').then((c) => c.BillingComponent),
            },
        ],
    },
    {
        path: GknRoutePath.DIGITAL_ASSISTANT,
        loadComponent: () => import('./chat/chat.component').then((c) => c.ChatComponent),
        canActivate: [OktaAuthGuard, digitalAssistantGuard],
    },
    {
        path: GknRoutePath.RENEWAL,
        canActivate: [OktaAuthGuard, renewalFlowGuard],
        children: [
            {
                path: ChildPageRoute.RENEWAL_THANK_YOU,
                loadComponent: () =>
                    import('./renewal/thank-you/thank-you.component').then((c) => c.RenewalThankYouComponent),
            },
            {
                path: ChildPageRoute.RENEWAL_ERROR,
                loadComponent: () => import('./renewal/error/error.component').then((c) => c.RenewalErrorComponent),
            },
            {
                path: '',
                loadComponent: () =>
                    import('./renewal/renewal-router/renewal-router.component').then((c) => c.RenewalRouterComponent),
            },
        ],
    },
    // Development-only routes
    ...(!environment.production
        ? [
              {
                  path: GknRoutePath.DESIGN,
                  children: [
                      {
                          path: 'buttons',
                          loadComponent: () =>
                              import('./design/cards/design-buttons/design-buttons.component').then(
                                  (c) => c.DesignButtonsComponent,
                              ),
                      },
                      {
                          path: 'status-badges',
                          loadComponent: () =>
                              import('./design/cards/design-status-badges/design-status-badges.component').then(
                                  (c) => c.DesignStatusBadgesComponent,
                              ),
                      },
                      {
                          path: 'typography',
                          loadComponent: () =>
                              import('./design/cards/design-typography/design-typography.component').then(
                                  (c) => c.DesignTypographyComponent,
                              ),
                      },
                      {
                          path: 'colors',
                          loadComponent: () =>
                              import('./design/cards/design-colors/design-colors.component').then(
                                  (c) => c.DesignColorsComponent,
                              ),
                      },
                      {
                          path: '',
                          loadComponent: () => import('./design/design.component').then((c) => c.DesignComponent),
                      },
                  ],
              },
              {
                  path: GknRoutePath.STATUS,
                  loadComponent: () => import('./status/status.component').then((m) => m.StatusComponent),
              },
              // Education Support (Not actually routed to anywhere in the app; exposed on the Education site)
              {
                  path: GknRoutePath.EDUCATION_SUPPORT,
                  loadComponent: () =>
                      import('./education/support/education-support.component').then(
                          (c) => c.EducationSupportComponent,
                      ),
              },
              {
                  path: GknRoutePath.RENEWAL_TEST,
                  canActivate: [renewalFlowGuard],
                  loadComponent: () =>
                      import('./renewal/renewal-test-utility/renewal-test-utility.component').then(
                          (c) => c.RenewalTestUtilityComponent,
                      ),
              },
              {
                  path: 'kepler-test',
                  canActivate: [OktaAuthGuard],
                  loadComponent: () =>
                      import('./kepler-test-utility/kepler-test-utility.component').then(
                          (c) => c.KeplerTestUtilityComponent,
                      ),
              },
          ]
        : []),
];
